import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All topics' },
    { id: 'searching', name: 'Rental hunting fees' },
    { id: 'selling', name: 'Asset listings (10k UGX)' },
    { id: 'payments', name: 'Payments and refunds' },
    { id: 'security', name: 'Broker verification' }
  ];

  const faqs = [
    {
      category: 'searching',
      question: 'Who actually holds my brokerage fee?',
      answer: 'HireBroker holds it. In the app you request a rental, choose the area and inspection day, and pay the full brokerage fee to HireBroker — not to the realtor. HireBroker is the merchant that receives the payment. The remaining fee is paid to the realtor only after you accept a property. This hold is for rental hunting only. HireBroker does not hold money for land transactions. Full detail: /how-fees-work.'
    },
    {
      category: 'searching',
      question: 'Is the money kept separately from operational funds?',
      answer: 'HireBroker holds your brokerage fee for the rental hunt. From that fee, HireBroker may pay the assigned realtor’s transport costs for inspections. The remaining balance stays with HireBroker until you accept a property, or until a rematch or refund is completed. This is a HireBroker-held rental hunting fee, not a licensed third-party escrow account and not a land or purchase-price trust.'
    },
    {
      category: 'searching',
      question: 'What happens if the agent fails to deliver, or I reject the properties?',
      answer: 'Tell HireBroker. After we verify the issue with the previous realtor, we can assign another agent, realtor, or broker, or refund the remaining balance. Transport already paid from the fee is not part of that remaining balance. The realtor is not paid the remaining brokerage fee unless you accept a property.'
    },
    {
      category: 'payments',
      question: 'How long does a refund take, and what happens in a dispute?',
      answer: 'HireBroker first verifies the issue. After a refund of the remaining balance is approved, it is sent to the original payment method and usually arrives within 3–7 business days. In a dispute, email support@hirebroker.co or call +256 774 509 859. HireBroker verifies the previous realtor’s issue, then rematches or refunds the balance. Unresolved disputes follow the Terms of Service.'
    },
    {
      category: 'selling',
      question: 'What is included in the flat 10,000 UGX listing model?',
      answer: 'For a single non-recurring payment of 10,000 UGX, your rental asset or land plot is shown to matching verified area brokers. That listing fee is a broadcast fee paid to HireBroker. It is not a rental hunting brokerage fee, and HireBroker does not hold land purchase money.'
    },
    {
      category: 'security',
      question: 'How do you filter out fraudulent street brokers from the ecosystem?',
      answer: 'Casual agents are strictly prohibited. Every broker on our platform undergoes a baseline identity check, zone expertise audit, and operates under strict feedback rating protocols monitored directly by platform administration.'
    }
  ];

  const filteredFaqs = activeCategory === 'all'
    ? faqs
    : faqs.filter(faq => faq.category === activeCategory);

  const searchedFaqs = searchQuery
    ? filteredFaqs.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredFaqs;

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Knowledge Base & Infrastructure
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6">
                System operations <span className="font-serif italic text-neutral-500 block sm:inline">documentation.</span>
              </h1>
              <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-xl">
                How rental hunting fees are held, when a realtor is paid, listing fees, and what to do if a hunt goes wrong.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: INTERACTIVE MATRIX SEARCH (Soft Chalk Grey Background) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-24">
          <div className="max-w-4xl mx-auto px-6">
            
            {/* Minimal Search Field */}
            <div className="relative max-w-2xl mx-auto mb-12">
              <input
                type="search"
                placeholder="Search hunting fees, refunds, listings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white px-5 py-4 pl-12 text-base text-neutral-900 border border-neutral-200 rounded-none focus:outline-none focus:border-neutral-950 transition-colors shadow-sm"
              />
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Architecture Category Filtering Tabulation */}
            <div className="flex flex-wrap gap-2 mb-12 justify-center border-b border-neutral-200/40 pb-6">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider transition-all duration-150 rounded-none border ${
                    activeCategory === category.id
                      ? 'bg-neutral-950 border-neutral-950 text-white font-medium'
                      : 'bg-white border-neutral-200 text-neutral-500 hover:text-neutral-950 hover:border-neutral-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Architectural FAQ Accordion Structure */}
            <div className="space-y-4">
              {searchedFaqs.length > 0 ? (
                searchedFaqs.map((faq, index) => (
                  <details 
                    key={index} 
                    className="group bg-white border border-neutral-200/60 open:border-neutral-300 transition-all"
                  >
                    <summary className="list-none flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-neutral-950 text-sm sm:text-base hover:bg-neutral-50 transition-colors">
                      <div className="flex gap-3">
                        <span className="text-red-600 font-mono text-sm pt-0.5 group-open:hidden">＋</span>
                        <span className="text-red-600 font-mono text-sm pt-0.5 hidden group-open:inline">－</span>
                        <span>{faq.question}</span>
                      </div>
                    </summary>
                    <div className="px-6 pb-6 pt-2 text-sm text-neutral-500 font-light leading-relaxed border-t border-neutral-100 pl-11">
                      {faq.answer}
                    </div>
                  </details>
                ))
              ) : (
                <div className="text-center py-12 bg-white border border-neutral-200/40">
                  <span className="text-xs uppercase tracking-widest text-neutral-400 font-mono block mb-2">
                    Zero Node Results
                  </span>
                  <p className="text-sm text-neutral-500 font-light">
                    Adjust query arguments or filter for alternative systemic operational sections above.
                  </p>
                </div>
              )}
            </div>

          </div>
        </section>


        {/* SECTION 3: CORE OPERATIONAL STATS (Pristine White Background) */}
        <section className="bg-white py-24 border-b border-neutral-100">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-12 text-left">
              {[
                { title: 'HireBroker holds the hunting fee', description: 'You pay HireBroker in the app. The realtor is paid the remaining fee only after you accept the rental.' },
                { title: 'Transport, rematch, or refund', description: 'Realtor transport comes from the fee. If there is a verified issue, HireBroker assigns another realtor or refunds the remaining balance.' },
                { title: 'Rental hunting only', description: 'This hold applies to rental hunts. HireBroker does not hold money for land transactions.' }
              ].map((item, index) => (
                <div key={index} className="space-y-3 border-l-2 border-neutral-100 pl-6 focus-within:border-red-600 transition-colors">
                  <h3 className="text-base font-medium text-neutral-950 tracking-tight">{item.title}</h3>
                  <p className="text-sm text-neutral-500 font-light leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 4: DIRECT CONNECT SUPPORT BLOCK (Warm Muted Grey Background) */}
        <section className="bg-neutral-100/70 py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="mb-12">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-2">
                Escalation Matrix
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight">Need granular transaction help?</h2>
              <p className="text-sm text-neutral-500 font-light mt-2 max-w-md mx-auto">
                If structural listing friction or transaction processing variations happen, reach an administrator instantly.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                { label: 'Security & Payout Desk', value: 'support@hirebroker.co' },
                { label: 'Direct Operations Call', value: '+256 774 509 859' },
                { label: 'System Live Chat Link', value: 'Available 24/7 Monitored' }
              ].map((contact, index) => (
                <div key={index} className="bg-white border border-neutral-200/50 p-6 shadow-sm flex flex-col justify-center">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium block mb-1">
                    {contact.label}
                  </span>
                  <span className="text-sm font-medium text-neutral-950 break-all">
                    {contact.value}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/how-fees-work"
                className="inline-flex items-center justify-center px-8 py-3.5 border border-neutral-300 text-neutral-950 text-sm font-medium tracking-tight hover:border-neutral-950 transition-colors duration-200"
              >
                How hunting fees work
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-8 py-3.5 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-200"
              >
                Contact support
              </a>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default HelpCenter;